import type { ReactNode } from 'react'

import { UpdateBanner } from './UpdateBanner'

interface PhoneFrameProps {
  children: ReactNode
}

/**
 * The app is phone-scope. On a desktop viewport it centres in a 430px column,
 * and there is no desktop UI — by design, not by omission.
 *
 * Three details carry the whole structure:
 *
 * - `relative`, so an overlay can be `absolute inset-0` inside it. A scrim is
 *   then bounded by the column *by construction*; nothing has to remember to
 *   keep it there.
 * - a full-height column, `height: 100dvh`, styled in `index.css` on
 *   `[data-phone-frame]` alongside the other layout invariants. On mobile Safari
 *   `100vh` is the *largest* viewport, so a bottom bar hides under the URL bar
 *   until the user scrolls; `dvh` tracks the visible area. There is no `vh`
 *   fallback: the build's target floor is already above `dvh` support, so
 *   lightningcss dropped the fallback line from the artefact anyway.
 * - `overflow-hidden`, so the column never scrolls. Scrolling belongs to
 *   `<main>` inside `AppShell`, which is what keeps the tab bar in normal flow
 *   and out of fixed positioning. (`check:layout` blanks comments before it
 *   scans, so prose is safe; the CI grep does not, which is why this comment
 *   still avoids spelling the property out.)
 *
 * SUAP-0104.
 */
export function PhoneFrame({ children }: PhoneFrameProps) {
  return (
    <div
      data-phone-frame
      className="relative mx-auto flex w-full max-w-[430px] flex-col overflow-hidden bg-ground"
    >
      {/* Inside the column, above everything else in it. An update banner mounted
          *outside* the frame adds its height to a 100dvh column and pushes the tab
          bar below the fold — measured at 905px against an 844px viewport before
          this moved (SUAP-0106 review). Every column carries one, the same rule as
          the overlay root. */}
      <UpdateBanner />
      {children}
    </div>
  )
}
